import { createSelector } from '@reduxjs/toolkit';

import { BearingEnvelope } from '@paragon/contract-library';

import { getRenderModeString, ManualRenderModes } from '../../library/renderModes';
import { getSelectedItemIdentifiers } from '../../library/selection/selectors';
import { RootState } from '../index';
import { isTopOrBottomCutPickingMode } from '../slices/applicationSlice';
import { CeilingRenderMode, EnvelopeRenderMode, RoofRenderMode } from '../slices/visualOptionsSlice';
import {
  getVisibleFeaturePatternContainerCount,
  getVisibleRoofContainerCount,
  getVisibleRoofPlaneCount,
  getVisibleTrussEnvelopeCount,
} from './layoutDataSelectors';
import { isLayoutBasedOnRoofPlanes } from './layoutModeSelectors';
import { getPickingObjectInfo, getPickingObjectInfoType } from './pickingSelectors';
import { getActiveSnappingGuideLines } from './snappingGuideLineSelectors';

export const getConfiguredFeaturePatternContainerMode = (state: RootState) =>
  getRenderModeString(state.visualOptions.featurePatternContainerMode);
export const getConfiguredFeatureAreaMode = (state: RootState) =>
  getRenderModeString(state.visualOptions.featureAreaMode);
export const getConfiguredRoofMode = (state: RootState) => getRenderModeString(state.visualOptions.roofMode);
export const getConfiguredCeilingMode = (state: RootState) => getRenderModeString(state.visualOptions.ceilingMode);
export const getConfiguredEnvelopeMode = (state: RootState) => getRenderModeString(state.visualOptions.envelopeMode);
export const getConfiguredWallMode = (state: RootState) => getRenderModeString(state.visualOptions.wallMode);
export const getConfiguredNonStructuralBearingMode = (state: RootState) =>
  getRenderModeString(state.visualOptions.nonStructuralBearingMode);
export const getConfiguredBeamMode = (state: RootState) => getRenderModeString(state.visualOptions.beamMode);
export const getConfiguredConnectionMode = (state: RootState) =>
  getRenderModeString(state.visualOptions.connectionMode);
export const getConfiguredWorkLineMode = (state: RootState) => getRenderModeString(state.visualOptions.workLineMode);

const areRoofPlanesForcedVisibleForPicking = (state: RootState) => {
  const pickingObjectInfo = getPickingObjectInfo(state);
  if (!pickingObjectInfo || pickingObjectInfo.type !== 'pickingPlane') return false;

  return (
    pickingObjectInfo.targetInfo.type === 'roof-cut' ||
    pickingObjectInfo.targetInfo.type === 'fascia-height-reference' ||
    pickingObjectInfo.targetInfo.type === 'corner-set-plane-1' ||
    pickingObjectInfo.targetInfo.type === 'corner-set-plane-2'
  );
};

const areCeilingPlanesForcedVisibleForPicking = (state: RootState) => {
  const pickingObjectInfo = getPickingObjectInfo(state);
  if (!pickingObjectInfo || pickingObjectInfo.type !== 'pickingPlane') return false;

  return pickingObjectInfo.targetInfo.type === 'ceiling-cut';
};

const getVisibleFeatureAreas = createSelector(
  (state: RootState) => state.layout.featurePatternContainers,
  (containers) => Object.values(containers).flatMap((container) => container.featureAreas),
);

const getVisibleFeatureAreaCount = createSelector(getVisibleFeatureAreas, (featureAreas) => featureAreas.length);

const isSelectionWindowToolActive = (state: RootState) => state.selections.isSelectionWindowToolEnabled;
const getSelectionWindowTarget = (state: RootState) => state.selections.selectionWindowSettings.layout;

const getSelectionWindowTargetIfActive = createSelector(
  [isSelectionWindowToolActive, getSelectionWindowTarget],
  (isActive, target) => (isActive ? target : null),
);

export const getActiveFeaturePatternContainerMode = createSelector(
  getConfiguredFeaturePatternContainerMode,
  getVisibleTrussEnvelopeCount,
  getVisibleFeatureAreaCount,
  (state: RootState) => state.application.insertingObjectInfo?.type,

  (configuredMode, trussEnvelopeCount, featureAreaCount, insertionObjectType) => {
    if (insertionObjectType === 'featurePatternContainer') {
      return 'translucent';
    }

    if (insertionObjectType === 'featureArea' || insertionObjectType === 'trussEnvelope') {
      // must be in wireframe mode in these cases so that the feature area can be interacted with
      return 'wireframe';
    }

    if (configuredMode === 'auto') {
      // start in translucent mode while containers are being created, and switch to wireframe mode once children are
      //   being added
      return trussEnvelopeCount === 0 && featureAreaCount === 0 ? 'translucent' : 'wireframe';
    }

    return configuredMode;
  },
);

export const getActiveFeatureAreaMode = createSelector(
  getConfiguredFeatureAreaMode,

  (state: RootState) => state.application.insertingObjectInfo?.type === 'featureArea',
  getSelectedItemIdentifiers,
  getSelectionWindowTargetIfActive,

  (configuredMode, isInsertingFeatureArea, selectedItems, selectionWindowTarget) => {
    if (isInsertingFeatureArea) {
      return 'translucent';
    }

    // ensure visible when selected or targeted by the selection window tool
    if (selectedItems.some((item) => item.type === 'featureArea') || selectionWindowTarget === 'featureArea') {
      if (configuredMode === 'none' || configuredMode === 'wireframe') return 'solid';
    }

    if (configuredMode === 'auto') {
      return 'solid';
    }

    return configuredMode;
  },
);

export const getActiveRoofMode = createSelector(
  getConfiguredRoofMode,

  getVisibleTrussEnvelopeCount,

  (state: RootState) => state.application.insertingObjectInfo?.type === 'roofPlane',
  (state: RootState) => state.application.insertingObjectInfo?.type === 'trussEnvelope',

  getPickingObjectInfo,
  areRoofPlanesForcedVisibleForPicking,

  getSelectedItemIdentifiers,
  getSelectionWindowTargetIfActive,

  (
    configuredMode,

    trussEnvelopeCount,

    isInsertingRoofPlane,
    isInsertingTrussEnvelope,

    pickingObjectInfo,
    areRoofPlanesForcedVisible,

    selectedItems,
    selectionWindowTarget,
  ): ManualRenderModes<RoofRenderMode> | 'none' => {
    if (isInsertingRoofPlane || areRoofPlanesForcedVisible) {
      return 'translucent';
    }

    // ensure visible when selected or targeted by the selection window tool
    if (selectedItems.some((item) => item.type === 'roofPlane') || selectionWindowTarget === 'roofPlane') {
      if (configuredMode === 'none' || configuredMode === 'wireframe' || configuredMode === 'auto')
        return 'translucent';
    }

    if (configuredMode === 'auto') {
      if (
        pickingObjectInfo?.type === 'pickingPlane' &&
        isTopOrBottomCutPickingMode(pickingObjectInfo.targetInfo.type)
      ) {
        return 'translucent';
      }

      // We want to display plane base visuals early on, then switch to a more unobtrusive mode by default once the user
      //   starts adding truss envelopes.
      return trussEnvelopeCount === 0 && !isInsertingTrussEnvelope ? 'translucent' : 'wireframe';
    }

    return configuredMode;
  },
);

export const getActiveCeilingMode = createSelector(
  isLayoutBasedOnRoofPlanes,
  getConfiguredCeilingMode,
  (state: RootState) => state.application.insertingObjectInfo?.type === 'roofPlane',
  getPickingObjectInfo,
  areCeilingPlanesForcedVisibleForPicking,
  getSelectedItemIdentifiers,
  getSelectionWindowTargetIfActive,

  (state: RootState) => Object.keys(state.layout.trussEnvelopes).length,
  (state: RootState) => state.application.insertingObjectInfo?.type === 'trussEnvelope',

  (
    inRoofPlaneProject,
    configuredMode,
    isInsertingRoofPlane,
    pickingObjectInfo,
    areCeilingPlanesPickable,
    selectedItems,
    selectionWindowTarget,

    trussEnvelopeCount,
    isInsertingTrussEnvelope,
  ): ManualRenderModes<CeilingRenderMode> | 'none' => {
    if (!inRoofPlaneProject) {
      return 'none';
    }

    if (isInsertingRoofPlane || areCeilingPlanesPickable) {
      return 'translucent';
    }

    // ensure visible when selected or targeted by the selection window tool
    if (selectedItems.some((item) => item.type === 'ceilingPlane') || selectionWindowTarget === 'ceilingPlane') {
      if (configuredMode === 'none' || configuredMode === 'wireframe' || configuredMode === 'auto')
        return 'translucent';
    }

    if (configuredMode === 'auto') {
      if (
        pickingObjectInfo?.type === 'pickingPlane' &&
        isTopOrBottomCutPickingMode(pickingObjectInfo.targetInfo.type)
      ) {
        return 'translucent';
      }

      // currently we intentionally mirror the automatic roof plane mode here, and it is up to the user to manually set
      //   render modes when they desire to exclusively work with ceiling planes or roof planes
      return trussEnvelopeCount === 0 && !isInsertingTrussEnvelope ? 'translucent' : 'wireframe';
    }

    return configuredMode;
  },
);

export const getActiveEnvelopeMode = createSelector(
  getConfiguredEnvelopeMode,

  getSelectedItemIdentifiers,
  getSelectionWindowTargetIfActive,

  (configuredMode, selectedItems, selectionWindowTarget): ManualRenderModes<EnvelopeRenderMode> | 'none' => {
    if (configuredMode === 'auto') {
      return 'components';
    }

    // ensure visible when selected or targeted by the selection window tool
    if (selectedItems.some((item) => item.type === 'trussEnvelope') || selectionWindowTarget === 'trussEnvelope') {
      if (configuredMode === 'none') return 'envelope';
    }

    return configuredMode;
  },
);

export const getActiveConnectionMode = createSelector(
  getConfiguredConnectionMode,

  (configuredMode) => {
    if (configuredMode === 'auto') {
      return 'hardware';
    }

    return configuredMode;
  },
);

export const getActiveWallMode = createSelector(
  getConfiguredWallMode,

  getSelectedItemIdentifiers,
  getSelectionWindowTargetIfActive,

  (configuredMode, selectedItems, selectionWindowTarget) => {
    if (configuredMode === 'auto') return 'hatched-wireframe';

    // ensure visible when selected or targeted by the selection window tool
    if (selectedItems.some((item) => item.type === 'bearing') || selectionWindowTarget === 'wall') {
      if (configuredMode === 'none') return 'hatched-wireframe';
    }

    return configuredMode;
  },
);

export const getActiveNonStructuralWallMode = createSelector(
  getConfiguredNonStructuralBearingMode,

  getSelectedItemIdentifiers,
  getSelectionWindowTargetIfActive,

  (configuredMode, selectedItems, selectionWindowTarget) => {
    if (configuredMode === 'auto') return 'hatched-wireframe';

    // ensure visible when selected or targeted by the selection window tool
    if (selectedItems.some((item) => item.type === 'bearing') || selectionWindowTarget === 'wall') {
      if (configuredMode === 'none') return 'hatched-wireframe';
    }

    return configuredMode;
  },
);

export const getActiveBeamMode = createSelector(
  getConfiguredBeamMode,

  getSelectedItemIdentifiers,
  getSelectionWindowTargetIfActive,

  (configuredMode, selectedItems, selectionWindowTarget) => {
    if (configuredMode === 'auto') return 'hatched-wireframe';

    // ensure visible when selected or targeted by the selection window tool
    if (selectedItems.some((item) => item.type === 'bearing') || selectionWindowTarget === 'beam') {
      if (configuredMode === 'none') return 'hatched-wireframe';
    }

    return configuredMode;
  },
);

export function getActiveRenderModeForBearingEnvelope<T extends string>(
  envelope: BearingEnvelope,
  structuralMode: T,
  nonStructuralMode: T,
  beamMode: T,
): T {
  if (envelope.bearingType === 'Beam') return beamMode;
  return envelope.nonStructural ? nonStructuralMode : structuralMode;
}

export const getActiveWorkLineMode = createSelector(
  getConfiguredWorkLineMode,
  getSelectedItemIdentifiers,
  getSelectionWindowTargetIfActive,
  (state: RootState) => getPickingObjectInfoType(state) === 'pickingLayoutLine',
  (state: RootState) => state.application.insertingObjectInfo?.type === 'workLine',
  (configuredMode, selectedItems, selectionWindowTarget, pickingLayoutLine, insertingWorkLine) =>
    insertingWorkLine ||
    pickingLayoutLine ||
    configuredMode === 'auto' ||
    selectedItems.some((item) => item.type === 'workLine') ||
    selectionWindowTarget === 'workLine'
      ? 'visible'
      : configuredMode,
);

export const getDoSnappingGuideLinesExist = createSelector(getActiveSnappingGuideLines, (lines) => lines.length > 0);

export const getActiveGridMode = createSelector(
  getVisibleRoofContainerCount,
  getVisibleRoofPlaneCount,
  getVisibleFeaturePatternContainerCount,

  getDoSnappingGuideLinesExist,

  (roofContainerCount, roofPlanesCount, featurePatternContainersCount, areSnappingGuidelinesActive) => {
    // grid lines heavily conflict with snapping guidelines visually
    if (areSnappingGuidelinesActive) return 'hide';

    // In an "empty" project before roof/floor geometries are created, it makes sense to show a grid since we're still
    // at the stage of drawing bearing envelopes.
    return roofContainerCount === 0 && roofPlanesCount === 0 && featurePatternContainersCount === 0 ? 'show' : 'hide';
  },
);
